/**
 * BulkActionBar - Floating action bar for bulk loan operations
 *
 * Appears when loans are selected, providing:
 * - Count of selected loans
 * - Dropdown to set reason for all selected
 * - Clear selection button
 */

import React, { useState } from 'react';
import {
  LossReason,
  LOSS_REASON_LABELS,
  STANDARD_LOSS_REASONS,
} from '../../hooks/useLostLoans';
import {
  Check,
  X,
  ChevronDown,
  Tag,
} from 'lucide-react';

interface BulkActionBarProps {
  selectedCount: number;
  onSetReason: (reason: LossReason, notes?: string) => Promise<boolean>;
  onClearSelection: () => void;
  isSaving: boolean;
}

const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedCount,
  onSetReason,
  onClearSelection,
  isSaving,
}) => {
  const [showReasonDropdown, setShowReasonDropdown] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState<LossReason | null>(null);
  const [notes, setNotes] = useState('');

  if (selectedCount === 0) return null;

  const handleReasonSelect = (reason: LossReason) => {
    setSelectedReason(reason);
    setShowReasonDropdown(false);
    setShowNotesModal(true);
  };

  const handleConfirm = async () => {
    if (!selectedReason) return;
    const success = await onSetReason(selectedReason, notes || undefined);
    if (success) {
      setShowNotesModal(false);
      setSelectedReason(null);
      setNotes('');
    }
  };

  const handleCancel = () => {
    setShowNotesModal(false);
    setSelectedReason(null);
    setNotes('');
  };

  return (
    <>
      {/* Fixed bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-blue-500/30 shadow-lg shadow-black/50 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Selection count */}
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white font-bold text-sm">
                {selectedCount}
              </div>
              <span className="text-white font-medium">
                loan{selectedCount !== 1 ? 's' : ''} selected
              </span>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              {/* Set Reason Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowReasonDropdown(!showReasonDropdown)}
                  disabled={isSaving}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Tag className="w-4 h-4" />
                  Set Reason
                  <ChevronDown className={`w-4 h-4 transition-transform ${showReasonDropdown ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown menu */}
                {showReasonDropdown && (
                  <>
                    {/* Backdrop */}
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowReasonDropdown(false)}
                    />
                    {/* Menu */}
                    <div className="absolute bottom-full mb-2 right-0 w-64 bg-gray-800 rounded-lg border border-gray-700 shadow-xl z-20 py-1 max-h-80 overflow-y-auto">
                      {STANDARD_LOSS_REASONS.map((reason) => (
                        <button
                          key={reason}
                          onClick={() => handleReasonSelect(reason)}
                          className="w-full px-4 py-2 text-left text-sm text-white hover:bg-gray-700 transition-colors"
                        >
                          {LOSS_REASON_LABELS[reason]}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Clear Selection */}
              <button
                onClick={onClearSelection}
                disabled={isSaving}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50 transition-colors"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Notes Confirmation Modal */}
      {showNotesModal && selectedReason && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 max-w-md w-full shadow-xl">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-white mb-2">
                Set Loss Reason
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                Set <span className="text-blue-400 font-medium">{LOSS_REASON_LABELS[selectedReason]}</span> as
                the reason for <span className="text-white font-medium">{selectedCount}</span> loan{selectedCount !== 1 ? 's' : ''}.
              </p>

              {/* Notes input */}
              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-1">
                  Notes (optional)
                </label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes about these loans..."
                  disabled={isSaving}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                />
              </div>

              {/* Action buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleConfirm}
                  disabled={isSaving}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSaving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Confirm
                    </>
                  )}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Spacer to prevent content from being hidden behind fixed bar */}
      <div className="h-16" />
    </>
  );
};

export default BulkActionBar;
